import { useState } from "react"
import type { UseFormReturn } from "react-hook-form"

import type { EquivalentProductGroupFormValues } from "../forms/equivalentProductGroupSchema"
import { serializeCreateInput, serializeUpdateInput } from "../utils"

import { useEquivalentProductGroupActions } from "./useEquivalentProductGroupActions"

import type { EquivalencyGroup } from "@/graphql/codegen/graphql"

interface UseEquivalentProductGroupDialogActionsProps {
  onSuccess: ((data: EquivalencyGroup) => Promise<void>) | undefined
  onClose: () => void
  methods: UseFormReturn<EquivalentProductGroupFormValues>
  data?: EquivalencyGroup
}

interface UseEquivalentProductGroupDialogActionsReturn {
  setIsCancelConfirmationOpen: (isOpen: boolean) => void
  isCancelConfirmationOpen: boolean
  handleSubmit: () => Promise<void>
  canSubmit: boolean
  handleCancel: () => void
}

const useEquivalentProductGroupDialogActions = ({
  onSuccess,
  onClose,
  methods,
  data,
}: UseEquivalentProductGroupDialogActionsProps): UseEquivalentProductGroupDialogActionsReturn => {
  const {
    handleSubmit: zodHandleSubmit,
    formState: { isValid, isDirty, errors, isSubmitting },
  } = methods

  const isCreateMode = !data
  const [isCancelConfirmationOpen, setIsCancelConfirmationOpen] = useState(false)

  const { createEquivalentProductGroup, updateEquivalentProductGroup } = useEquivalentProductGroupActions({
    onSuccess: async (group) => {
      if (group) {
        await onSuccess?.(group)
      }
      onClose()
    },
  })

  const handleSubmit = zodHandleSubmit(async (input: EquivalentProductGroupFormValues) => {
    if (isCreateMode) {
      await createEquivalentProductGroup(serializeCreateInput(input))
    } else {
      await updateEquivalentProductGroup(serializeUpdateInput(input, data.equivalencyGroupId))
    }
  })

  const handleCancel = () => {
    if (isDirty) {
      setIsCancelConfirmationOpen(true)
    } else {
      onClose()
    }
  }

  // Ensure the form is valid, dirty, and has no errors before allowing submission
  const canSubmit = isValid && isDirty && !Object.keys(errors).length && !isSubmitting

  return {
    setIsCancelConfirmationOpen,
    isCancelConfirmationOpen,
    canSubmit,
    handleSubmit,
    handleCancel,
  }
}

export default useEquivalentProductGroupDialogActions
